import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import { search as searchApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import {
  Search, Bell, X, ShoppingBag, Package, RotateCcw,
  AlertTriangle, AlertOctagon, Info, ChevronRight, Menu, Sun, Moon
} from 'lucide-react';

const TYPE_ICONS: Record<string, React.ReactNode> = {
  order: <ShoppingBag size={14} />,
  inventory: <Package size={14} />,
  rto: <RotateCcw size={14} />,
};

const TYPE_COLORS: Record<string, string> = {
  order: 'var(--accent-blue)',
  inventory: 'var(--accent-green)',
  rto: 'var(--accent-purple)',
};

const NOTIF_ICONS: Record<string, React.ReactNode> = {
  critical: <AlertOctagon size={14} />,
  warning: <AlertTriangle size={14} />,
  info: <Info size={14} />,
};

const NOTIF_COLORS: Record<string, { color: string; soft: string }> = {
  critical: { color: 'var(--accent-red)', soft: 'var(--accent-red-soft)' },
  warning: { color: 'var(--accent-amber)', soft: 'var(--accent-amber-soft)' },
  info: { color: 'var(--accent-blue)', soft: 'var(--accent-blue-soft)' },
};

const Layout: React.FC<{ children: React.ReactNode; title: string; subtitle?: string }> = ({ children, title, subtitle }) => {
  const today = new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  // Mobile sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  // Notifications state
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchNotifs = () => {
      searchApi.notifications().then(r => {
        setNotifications(r.data.notifications || []);
        setUnreadCount(r.data.unread_count || 0);
      }).catch(() => {});
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (searchQuery.length < 2) { setSearchResults([]); setSearchOpen(false); return; }
    setSearchLoading(true);
    searchTimeout.current = setTimeout(() => {
      searchApi.global(searchQuery).then(r => {
        setSearchResults(r.data.results || []);
        setSearchOpen(true);
      }).catch(() => {}).finally(() => setSearchLoading(false));
    }, 300);
  }, [searchQuery]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setSearchOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (sidebarOpen) { document.body.style.overflow = 'hidden'; }
    else { document.body.style.overflow = ''; }
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  const handleResultClick = (result: any) => {
    setSearchOpen(false); setSearchQuery(''); navigate(result.link);
  };

  const formatTime = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  return (
    <div style={{ display: 'flex', background: 'var(--bg-primary)', minHeight: '100vh' }}>
      <div className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`} onClick={() => setSidebarOpen(false)} />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="layout-content" style={{ marginLeft: 260, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Top Bar */}
        <div className="layout-topbar" style={{
          padding: '16px 32px', borderBottom: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'var(--bg-secondary)', position: 'sticky', top: 0, zIndex: 30,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="show-mobile" onClick={() => setSidebarOpen(true)} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', width: 36, height: 36,
              display: 'none', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
            }}><Menu size={18} color="var(--text-secondary)" /></button>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: -0.3, lineHeight: 1.3 }}>{title}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{subtitle || today}</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Search — desktop only */}
            <div ref={searchRef} style={{ position: 'relative' }} className="hide-mobile">
              <div className="search-box" style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
                background: 'var(--bg-input)', border: `1px solid ${searchOpen ? 'var(--accent-blue)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-sm)', width: 260, transition: 'var(--transition)',
              }}>
                <Search size={14} color={searchOpen ? 'var(--accent-blue)' : 'var(--text-muted)'} />
                <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  onFocus={() => { if (searchResults.length) setSearchOpen(true); }}
                  placeholder="Search orders, inventory, RTO..."
                  style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: 13, outline: 'none', width: '100%' }} />
                {searchQuery && (
                  <button onClick={() => { setSearchQuery(''); setSearchOpen(false); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 0 }}>
                    <X size={13} color="var(--text-muted)" />
                  </button>
                )}
              </div>
              {searchOpen && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 6,
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-elevated)',
                  maxHeight: 400, overflowY: 'auto', zIndex: 50,
                }}>
                  {searchLoading ? (
                    <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Searching...</div>
                  ) : searchResults.length > 0 ? (
                    <>
                      <div style={{ padding: '10px 14px', fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, borderBottom: '1px solid var(--border)' }}>
                        {searchResults.length} results
                      </div>
                      {searchResults.map((r, i) => (
                        <div key={i} onClick={() => handleResultClick(r)} style={{
                          padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 12,
                          cursor: 'pointer', borderBottom: '1px solid var(--border)', transition: 'var(--transition)',
                        }}
                          onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-elevated)'}
                          onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                          <div style={{
                            width: 30, height: 30, borderRadius: 'var(--radius-sm)',
                            background: `${TYPE_COLORS[r.type]}15`, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', color: TYPE_COLORS[r.type], flexShrink: 0,
                          }}>{TYPE_ICONS[r.type]}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</div>
                            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 1 }}>{r.subtitle}</div>
                          </div>
                          <span style={{
                            fontSize: 9.5, padding: '2px 7px', borderRadius: 10, fontWeight: 600,
                            textTransform: 'uppercase', background: `${TYPE_COLORS[r.type]}15`, color: TYPE_COLORS[r.type],
                          }}>{r.type}</span>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No results for "{searchQuery}"</div>
                  )}
                </div>
              )}
            </div>

            {/* Theme Toggle */}
            <button className="theme-toggle" onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            {/* Notification Bell */}
            <div ref={notifRef} style={{ position: 'relative' }}>
              <div onClick={() => setNotifOpen(!notifOpen)} style={{
                width: 36, height: 36, borderRadius: 'var(--radius-sm)',
                background: notifOpen ? 'var(--accent-blue-soft)' : 'var(--bg-card)',
                border: `1px solid ${notifOpen ? 'rgba(79,125,249,0.2)' : 'var(--border)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', position: 'relative', transition: 'var(--transition)',
              }}>
                <Bell size={15} color={notifOpen ? 'var(--accent-blue)' : 'var(--text-secondary)'} />
                {unreadCount > 0 && (
                  <div style={{
                    position: 'absolute', top: 5, right: 5, width: 8, height: 8,
                    borderRadius: '50%', background: 'var(--accent-red)',
                    border: '2px solid var(--bg-secondary)',
                  }} />
                )}
              </div>
              {notifOpen && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 6,
                  width: 360, maxWidth: '90vw', background: 'var(--bg-card)',
                  border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-elevated)', maxHeight: 480, overflowY: 'auto', zIndex: 50,
                }}>
                  <div style={{
                    padding: '14px 16px', borderBottom: '1px solid var(--border)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    position: 'sticky', top: 0, background: 'var(--bg-card)',
                    borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Notifications</span>
                      {unreadCount > 0 && (
                        <span style={{
                          fontSize: 10.5, padding: '2px 8px', borderRadius: 10, fontWeight: 700,
                          background: 'var(--accent-red-soft)', color: 'var(--accent-red)',
                        }}>{unreadCount}</span>
                      )}
                    </div>
                    <button onClick={() => setNotifOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                      <X size={15} color="var(--text-muted)" />
                    </button>
                  </div>
                  {notifications.length > 0 ? (
                    notifications.map((n, i) => {
                      const nc = NOTIF_COLORS[n.type] || NOTIF_COLORS.info;
                      return (
                        <div key={i} onClick={() => { navigate(n.link); setNotifOpen(false); }} style={{
                          padding: '12px 16px', display: 'flex', gap: 12, cursor: 'pointer',
                          borderBottom: '1px solid var(--border)', transition: 'var(--transition)',
                        }}
                          onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-elevated)'}
                          onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                          <div style={{
                            width: 28, height: 28, borderRadius: 'var(--radius-sm)',
                            background: nc.soft, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', color: nc.color, flexShrink: 0, marginTop: 2,
                          }}>{NOTIF_ICONS[n.type]}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>{n.title}</div>
                            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 3 }}>{n.message}</div>
                            {n.time && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4, opacity: 0.7 }}>{formatTime(n.time)}</div>}
                          </div>
                          <ChevronRight size={13} color="var(--text-muted)" style={{ flexShrink: 0, marginTop: 6 }} />
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>All clear — no notifications</div>
                  )}
                </div>
              )}
            </div>

            {/* Avatar */}
            <div className="hide-mobile" style={{
              width: 36, height: 36, borderRadius: 'var(--radius-sm)',
              background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer',
            }}>B</div>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, padding: '24px 32px', overflowY: 'auto' }} className="layout-content-inner">{children}</div>
      </div>
    </div>
  );
};

export default Layout;
